/**
 * Inspector — 4-tab drawer for one focused section.
 *
 * Variant C's per-section deep-dive surface. The spine shows
 * progress; the inspector shows depth. One section is focused at a
 * time; tabs scope the view:
 *
 *   📄 Draft        — generated text + per-sentence "ask why" reveal
 *                     (Phase 3, here)
 *   ◉ Coverage     — subtask checklist (Phase 4 — wraps SubtaskCard)
 *   ⚑ Issues        — gap + regulatory ledger AND the regulatory-review run
 *                     control (the former "Review" tab was folded in here)
 *   📖 Instructions — raw template chunk (Phase 4)
 *
 * Layout decision (Phase 3): the inspector renders as a BOTTOM
 * panel under the spine, not inline under the focused row.
 *   Why: keeps SRP clean — spine renders the thread, inspector
 *   renders one section's depth. The mock's "inline under a node"
 *   treatment couples the two more tightly than a Word task pane
 *   (440px wide, single-column scroll) needs in v1. We can revisit
 *   in Phase 7 polish if the bottom-panel feels like the wrong
 *   metaphor in the live add-in.
 *
 * SRP: composes the tab shell + sub-tab components. Each tab owns
 * its own data fetch (Draft fetches section content, Coverage
 * uses CoverageTab, etc.). The inspector itself is presentational.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useState } from "react";
import type { SpineSection } from "../hooks/useVariantCRun";
import { formatGutterNumber } from "../sectionLabel";
import type { RunIdentity } from "../sseAdapter";
import { REVIEW_AGENT_ENABLED } from "../../config/reviewAgent";
import { useFlag } from "../../context/FeatureFlagsContext";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";
import ActionHistoryTab from "./inspector/ActionHistoryTab";
import CoverageTabPanel from "./inspector/CoverageTabPanel";
import DataGapsTabPanel from "./inspector/DataGapsTabPanel";
import DraftTab from "./inspector/DraftTab";
import InstructionsTab from "./inspector/InstructionsTab";

// ---------------------------------------------------------------------------
// Tab vocabulary
// ---------------------------------------------------------------------------

export type InspectorTab = "draft" | "coverage" | "gaps" | "instructions" | "actions";

interface TabMeta {
  key: InspectorTab;
  label: string;
  icon: string;
  /** True until the tab content lands in a later phase. */
  comingSoon?: boolean;
}

const TABS: TabMeta[] = [
  { key: "draft", label: "Draft", icon: "📄" },
  // Coverage is sidecar-backed (review_agent); hide it while the sidecar
  // kill switch is off so we don't poll the container. Flip
  // REVIEW_AGENT_ENABLED to restore the tab.
  ...(REVIEW_AGENT_ENABLED ? ([{ key: "coverage", label: "Coverage", icon: "◉" }] as TabMeta[]) : []),
  // Issues (data gaps + regulatory flags + the regulatory-review run control)
  // sits right after the section output (Draft). Key stays "gaps" for back-compat.
  // The former standalone "Review" tab was folded into this one.
  { key: "gaps", label: "Issues", icon: "⚑" },
  // Final instruction text served to the drafter, with conditional-block
  // activation verdicts (passed / excluded / unverified).
  { key: "instructions", label: "Instructions", icon: "📋" },
  // Context-preparation tool calls (internet + local library), grouped and
  // collapsed by default.
  { key: "actions", label: "Action History", icon: "🕑" },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface InspectorProps {
  /** The section the inspector is scoped to. */
  section: SpineSection;
  /** Run identity for the data fetches in each tab. */
  runId: string;
  /** Doc-type code (e.g. "CSR") — needed by useCitations + Instructions. */
  docCode: string;
  /** (trial, session) captured from the run_metadata SSE event.
   *  Coverage + Review tabs gate their renders on this. */
  identity: RunIdentity | null;
  /** Close handler — host clears focusedSectionId. */
  onClose: () => void;
  /** Spec 23 P10.5 — optional "Regenerate this section" affordance
   *  in the header. Shell passes a handler only when regen is
   *  meaningful for the current state (typically post-run); omit
   *  to hide the button. Host opens the RegenOne modal + routes
   *  the confirmed intent. */
  onRegenerate?: () => void;
  /** Passed instead of onRegenerate when the section has never been
   *  drafted. Renders a "Generate" button that routes to the
   *  "Draft more sections" picker rather than the regen modal. */
  onGenerate?: () => void;
  /** Monotonic signal that bumps each time the writer asks to open the
   *  Review tab for this section (e.g. clicking the spine's ``N⚖`` regulator
   *  badge). A change forces the drawer onto the Review tab — even when the
   *  section is already focused, where the section-change reset wouldn't
   *  fire. Ignored (stays on Draft) when undefined / 0. */
  openReviewSignal?: number;
  /** Monotonic signal that bumps each time a highlighted-span edit is approved
   *  for this section. A change re-fetches the Data Gaps tab's open-items list
   *  so it reflects the re-checked section. Ignored when undefined / 0. */
  issuesRefreshSignal?: number;
  /** Monotonic signal that bumps when this section's Action History may have
   *  changed (a lifecycle write, regen, resolve section, or run end). A change
   *  re-fetches the Action History tab. Ignored when undefined / 0. */
  actionsRefreshSignal?: number;
  /** True when another section is currently being regenerated. Used to
   *  disable Generate/Regenerate buttons while a regen is in progress
   *  to prevent concurrent regenerations. */
  isRegenInProgress?: boolean;
  /** Optional "Resolve with asthra" handler for the Data Gaps tab — seeds a
   *  chat turn that researches + resolves one gap marker. Hidden when
   *  absent (e.g. chat unavailable). */
  onResolveGapWithAsthra?: (issue: import("../../api/client").SectionIssue) => void;
  /** Live per-section agent step lines for the focused section (from the shell's
   *  agentActivityFeed). Drives the Issues tab's expandable "full step feed"
   *  while a regulatory review runs. Empty when no feed is active. */
  auditActivityLines?: readonly import("../services/agentActivityFeed").AgentActivityLine[];
  /** True when the current run is a custom submission (built on the writer's own
   *  Word document). Turns the Instructions tab into a per-section instruction
   *  EDITOR (custom sections have no template instruction to display). */
  isCustomSubmission?: boolean;
  /** Called after a custom section's instruction is saved/cleared so the shell
   *  can update the spine row that gates the Generate / Draft-more buttons. */
  onInstructionSaved?: (sectionId: string, instruction: string | null) => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: "0 0 auto",
    maxHeight: "60vh",
    minHeight: "200px",
    background: "#fff",
    borderTop: `1px solid ${surfaceColors.lineStrong}`,
    boxShadow: "0 -8px 24px -10px rgba(30,20,60,0.20)",
    fontFamily: fonts.body,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    background: surfaceColors.paper,
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
  },
  numberGutter: {
    fontSize: "10.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  title: {
    flex: 1,
    fontSize: "12.5px",
    fontWeight: 700,
    color: inkColors.ink,
    fontFamily: fonts.display,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  closeBtn: {
    fontSize: "14px",
    color: inkColors.ink4,
    cursor: "pointer",
    padding: "0 6px",
    background: "none",
    border: "none",
  },
  // Spec 23 P10.5 — header "Regenerate" button. Compact so it
  // doesn't dominate the header row; mono caps + brand-mid border
  // matches the rail's affordance language. Sits just left of ×.
  regenBtn: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: brandColors.primary,
    background: "#fff",
    border: `1px solid ${brandColors.mid}`,
    borderRadius: "3px",
    padding: "3px 8px",
    cursor: "pointer",
    "&:hover": {
      background: brandColors.light,
    },
  },
  tabs: {
    display: "flex",
    flexWrap: "nowrap",
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontSize: "10.5px",
    flexShrink: 0,
    overflowX: "auto",
    overflowY: "hidden",
    // Thin, unobtrusive horizontal scrollbar for the tab strip.
    scrollbarWidth: "thin",
    "::-webkit-scrollbar": {
      height: "4px",
    },
    "::-webkit-scrollbar-thumb": {
      backgroundColor: surfaceColors.line,
      borderRadius: "2px",
    },
  },
  tab: {
    flex: "0 0 auto",
    whiteSpace: "nowrap",
    padding: "8px 8px",
    fontWeight: 500,
    color: inkColors.ink4,
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    background: "none",
    border: "none",
    borderBottomWidth: "2px",
    borderBottomStyle: "solid",
    borderBottomColor: "transparent",
  },
  tabActive: {
    fontWeight: 700,
    color: brandColors.primary,
    borderBottomColor: brandColors.primary,
  },
  tabIcon: {
    fontSize: "11px",
  },
  tabLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
  },
  comingSoonAccent: {
    color: brandColors.primary,
    fontFamily: fonts.mono,
    fontWeight: 700,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Inspector: React.FC<InspectorProps> = ({
  section,
  runId,
  docCode,
  identity,
  onClose,
  onRegenerate,
  onGenerate,
  openReviewSignal,
  issuesRefreshSignal,
  actionsRefreshSignal,
  isRegenInProgress,
  onResolveGapWithAsthra,
  auditActivityLines,
  isCustomSubmission,
  onInstructionSaved,
}) => {
  const styles = useStyles();
  const [tab, setTab] = useState<InspectorTab>("draft");
  const flagEnabled = useFlag("@test.feature-flag");
  // Resetting to "draft" whenever the focused section changes keeps
  // navigation predictable — clicking around the spine always lands
  // on the writer's primary surface, not whatever tab they last opened.
  const sectionKey = section.id;
  React.useEffect(() => {
    void sectionKey; // trigger: reset tab when section changes
    setTab("draft" as InspectorTab);
  }, [sectionKey]);

  // Spine ``N⚖`` regulator-badge clicks bump openReviewSignal to force the
  // Issues tab open (the regulatory review now lives there). Declared AFTER the
  // section-reset effect on purpose: when a badge click changes the focused
  // section AND bumps the signal in the same commit, React runs effects in
  // declaration order — the reset writes "draft" first, then this writes
  // "gaps", so the Issues tab wins.
  React.useEffect(() => {
    if (!openReviewSignal) return; // undefined/0 → stay on Draft (prop contract)
    setTab("gaps" as InspectorTab);
  }, [openReviewSignal]);

  // Draft and Issues tabs always visible; filter other tabs based on feature flag
  const visibleTabs = React.useMemo(() => {
    if (flagEnabled) {
      return TABS.filter(
        (t) => t.key === "draft" || t.key === "gaps" || t.key === "instructions" || t.key === "actions",
      );
    }
    return TABS;
  }, [flagEnabled]);

  let body: React.ReactNode;
  switch (tab) {
    case "draft":
      body = <DraftTab section={section} runId={runId} docCode={docCode} identity={identity} />;
      break;
    case "coverage":
      body = <CoverageTabPanel identity={identity} moduleId={section.id} />;
      break;
    case "gaps":
      body = (
        <DataGapsTabPanel
          section={section}
          runId={runId}
          onResolveWithAsthra={onResolveGapWithAsthra}
          issuesRefreshSignal={issuesRefreshSignal}
          identity={identity}
          auditActivityLines={auditActivityLines}
        />
      );
      break;
    case "instructions":
      body = (
        <InstructionsTab
          section={section}
          runId={runId}
          isCustomSubmission={isCustomSubmission}
          onInstructionSaved={onInstructionSaved}
        />
      );
      break;
    case "actions":
      body = <ActionHistoryTab section={section} runId={runId} refreshSignal={actionsRefreshSignal} />;
      break;
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.numberGutter}>{formatGutterNumber(section.number)}</span>
        <span className={styles.title}>{section.title}</span>
        {onGenerate && (
          <button
            type="button"
            className={styles.regenBtn}
            onClick={onGenerate}
            disabled={isRegenInProgress}
            aria-label={`Generate section ${section.number}`}
            title={isRegenInProgress ? "Wait for the current section regeneration to complete" : "Draft this section"}
            style={isRegenInProgress ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            Generate
          </button>
        )}
        {onRegenerate && (
          <button
            type="button"
            className={styles.regenBtn}
            onClick={onRegenerate}
            disabled={isRegenInProgress}
            aria-label={`Regenerate section ${section.number}`}
            title={
              isRegenInProgress
                ? "Wait for the current section regeneration to complete"
                : "Re-draft this section from scratch"
            }
            style={isRegenInProgress ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            Regen
          </button>
        )}
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close inspector">
          ×
        </button>
      </div>

      <div className={styles.tabs} role="tablist">
        {visibleTabs.map((t) => (
          <button
            type="button"
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={mergeClasses(styles.tab, tab === t.key && styles.tabActive)}
            disabled={t.comingSoon}
            title={t.comingSoon ? `${t.label} — Phase 4` : t.label}
          >
            <span className={styles.tabIcon} aria-hidden="true">
              {t.icon}
            </span>
            <span className={styles.tabLabel}>{t.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.body}>{body}</div>
    </div>
  );
};

export default Inspector;
